import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { generateMatchRosterPDF } from '../utils/pdfRoster';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Team {
  id: string;
  name: string;
  teamNumber: string;
  colors: { primary: string; secondary: string };
  status: string;
  memberCount: number;
  manager: { id: string; firstName: string; lastName: string } | null;
  leagueId?: string;
  league?: { id: string; name: string } | null;
  club?: { id: string; name: string } | null;
}

interface NextMatch {
  teamId: string;
  matchId: string;
  scheduledDate: string;
  homeTeam: { id: string; name: string };
  awayTeam: { id: string; name: string };
}

const Teams: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState<Team[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [myTeams, setMyTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [leagues, setLeagues] = useState<Array<{ id: string; name: string }>>([]);
  const [nextMatches, setNextMatches] = useState<Record<string, NextMatch>>({});
  const [loadingRosters, setLoadingRosters] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        // Fetch all teams (league data now included in response via join)
        const [teamsResponse, myTeamsResponse] = await Promise.all([
          api.get('/teams', { params: { page: '1', limit: '100' } }),
          user ? api.get('/teams/my-teams').catch(() => ({ data: { teams: [] } })) : Promise.resolve({ data: { teams: [] } })
        ]);

        const teamsData = teamsResponse.data.teams || teamsResponse.data;
        setTeams(teamsData);
        setFilteredTeams(teamsData);

        if (user) {
          const myTeamsData = myTeamsResponse.data.teams || [];
          setMyTeams(myTeamsData);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
    fetchLeagues();
  }, [user]);

  // Fetch leagues for filter
  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues', { params: { limit: '100' } });
      const leaguesData = response.data.leagues || response.data;
      setLeagues(leaguesData.map((l: any) => ({ id: l.id, name: l.name })));
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  // Fetch next match for each team (for managers, refs, and admins)
  useEffect(() => {
    if (!user || (user.profileType !== 'manager' && !isAdminOrSuperAdmin(user.profileType) && user.profileType !== 'referee')) {
      return;
    }

    const fetchNextMatches = async () => {
      const matchesMap: Record<string, NextMatch> = {};
      
      // For managers, only fetch matches for their teams
      // For admins and refs, fetch for all teams on current page
      const teamsToCheck = user.profileType === 'manager' 
        ? teams.filter(t => t.manager?.id === user.id)
        : teams;

      if (teamsToCheck.length === 0) {
        return;
      }

      await Promise.all(
        teamsToCheck.map(async (team) => {
          try {
            const response = await api.get('/matches', {
              params: {
                teamId: team.id,
                status: 'scheduled',
                limit: '100'
              }
            });
            const matches = response.data.matches || response.data;
            
            // Find the next scheduled match (earliest date)
            const now = new Date();
            now.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
            const upcomingMatches = matches
              .filter((m: any) => {
                const matchDate = new Date(m.scheduledDate);
                matchDate.setHours(0, 0, 0, 0);
                return matchDate >= now;
              })
              .sort((a: any, b: any) => {
                const dateA = new Date(a.scheduledDate).getTime();
                const dateB = new Date(b.scheduledDate).getTime();
                return dateA - dateB;
              });

            if (upcomingMatches.length > 0) {
              const nextMatch = upcomingMatches[0];
              matchesMap[team.id] = {
                teamId: team.id,
                matchId: nextMatch.id,
                scheduledDate: nextMatch.scheduledDate,
                homeTeam: nextMatch.homeTeam || { id: nextMatch.homeTeamId, name: 'TBD' },
                awayTeam: nextMatch.awayTeam || { id: nextMatch.awayTeamId, name: 'TBD' }
              };
            }
          } catch (error) {
            console.error(`Error fetching next match for team ${team.id}:`, error);
          }
        })
      );

      setNextMatches(matchesMap);
    };

    fetchNextMatches();
  }, [teams, user]);
  

  const handlePrintNextGameRoster = async (teamId: string) => {
    const nextMatch = nextMatches[teamId];
    if (!nextMatch) {
      alert('No upcoming match found for this team');
      return;
    }

    setLoadingRosters({ ...loadingRosters, [teamId]: true });

    try {
      // Fetch roster data
      const response = await api.get(`/matches/${nextMatch.matchId}/roster`);
      const { match, homeTeamPlayers, awayTeamPlayers } = response.data;

      // Determine if this is the home or away team
      const isHomeTeam = match.homeTeam.id === teamId;
      const teamType = isHomeTeam ? 'home' : 'away';

      // Check if match is more than 3 days away
      const matchDate = new Date(nextMatch.scheduledDate);
      const now = new Date();
      const daysUntilMatch = Math.ceil((matchDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
      const addWatermark = daysUntilMatch > 3;

      await generateMatchRosterPDF(
        {
          homeTeam: match.homeTeam,
          awayTeam: match.awayTeam,
          scheduledDate: match.scheduledDate,
          scheduledTime: match.scheduledTime,
          location: match.location,
          league: match.league
        },
        homeTeamPlayers,
        awayTeamPlayers,
        teamType,
        addWatermark
      );
    } catch (error: any) {
      console.error('Error generating roster PDF:', error);
      alert(error.response?.data?.error || 'Failed to generate roster PDF');
    } finally {
      setLoadingRosters({ ...loadingRosters, [teamId]: false });
    }
  };

  const canPrintRoster = (team: Team): boolean => {
    if (!user) return false;
    if (isAdminOrSuperAdmin(user.profileType)) return true;
    if (user.profileType === 'referee') return true;
    if (user.profileType === 'manager' && team.manager?.id === user.id) return true;
    return false;
  };

  // Filter teams based on search term and league (client-side filtering, no API call)
  // This won't cause focus loss since it's just filtering local state
  useEffect(() => {
    let filtered = teams;

    // Apply league filter
    if (selectedLeagueId) {
      filtered = filtered.filter(team => team.leagueId === selectedLeagueId);
    }

    // Apply search term filter
    if (searchTerm.trim() !== '') {
      filtered = filtered.filter(team =>
        team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.teamNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.manager?.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.manager?.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        team.league?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredTeams(filtered);
  }, [searchTerm, selectedLeagueId, teams]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Teams</h1>
        {isAdminOrSuperAdmin(user?.profileType) && (
          <button className="btn btn-primary" onClick={() => navigate('/teams/create')}>
            Create New Team
          </button>
        )}
      </div>

      {myTeams.length > 0 && (
        <div style={{ marginBottom: '30px' }}>
          <h2>My Teams</h2>
          <p style={{ color: '#757575', marginBottom: '15px' }}>
            Teams you are associated with as a manager, player, captain, or coach
          </p>
          <div className="grid">
            {myTeams.map((team) => (
              <div key={team.id} className="team-card" style={{ cursor: 'pointer', borderColor: 'var(--color-primary)' }} onClick={() => navigate(`/teams/${team.id}`)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                  <div
                    style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      backgroundColor: team.colors.primary,
                      border: `3px solid ${team.colors.secondary}`
                    }}
                  />
                  <div>
                    <h3>{team.name}</h3>
                    <p style={{ color: '#757575', fontSize: '14px' }}>{team.teamNumber}</p>
                  </div>
                </div>
                <p><strong>Status:</strong> <span className={`badge ${team.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{team.status}</span></p>
                <p><strong>Members:</strong> {team.memberCount}</p>
                {team.manager?.id === user?.id && (
                  <p><strong>Your Role:</strong> <span className="badge badge-info">Manager</span></p>
                )}
                {team.club && (
                  <p>
                    <strong>Club:</strong>{' '}
                    <Link 
                      to={`/clubs/${team.club.id}`} 
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                    >
                      {team.club.name}
                    </Link>
                  </p>
                )}
                {team.league && (
                  <p>
                    <strong>League:</strong>{' '}
                    <Link 
                      to={`/leagues?leagueId=${team.league.id}`} 
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                    >
                      {team.league.name}
                    </Link>
                  </p>
                )}
                {team.manager && team.manager.id !== user?.id && (
                  <p><strong>Manager:</strong> {team.manager.firstName} {team.manager.lastName}</p>
                )}
                {canPrintRoster(team) && (
                  <button
                    className="btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePrintNextGameRoster(team.id);
                    }}
                    disabled={loadingRosters[team.id] || !nextMatches[team.id]}
                    style={{ marginTop: '10px', width: '100%' }}
                    title={!nextMatches[team.id] ? 'No upcoming match found' : ''}
                  >
                    {loadingRosters[team.id] ? 'Generating...' : nextMatches[team.id] ? '📄 Print Next Game Roster' : '📄 Print Next Game Roster (No Match)'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '15px' }}>
          <div className="form-group">
            <label htmlFor="team-search">Search Teams</label>
            <input
              id="team-search"
              type="text"
              placeholder="Search by team name, number, manager, or league..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '10px' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="league-filter">Filter by League</label>
            <select
              id="league-filter"
              value={selectedLeagueId}
              onChange={(e) => setSelectedLeagueId(e.target.value)}
              style={{ width: '100%' }}
            >
              <option value="">All Leagues</option>
              {leagues.map(league => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(searchTerm || selectedLeagueId) && (
          <p style={{ color: '#757575', fontSize: '14px', marginTop: '10px' }}>
            Showing {filteredTeams.length} team(s)
            {searchTerm && ` matching "${searchTerm}"`}
            {selectedLeagueId && ` in ${leagues.find(l => l.id === selectedLeagueId)?.name || 'selected league'}`}
          </p>
        )}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h2>All Teams ({filteredTeams.length} teams)</h2>
      </div>

      {filteredTeams.length === 0 && !loading ? (
        <div className="card">
          <p>No teams found{searchTerm || selectedLeagueId ? ' matching your search criteria' : ''}.</p>
        </div>
      ) : (
        <div className="grid">
          {filteredTeams.map((team) => (
          <div key={team.id} className="team-card" style={{ cursor: 'pointer' }} onClick={() => navigate(`/teams/${team.id}`)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
              <div
                style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '50%',
                  backgroundColor: team.colors.primary,
                  border: `3px solid ${team.colors.secondary}`
                }}
              />
              <div>
                <h3>{team.name}</h3>
                <p style={{ color: '#757575', fontSize: '14px' }}>{team.teamNumber}</p>
              </div>
            </div>
            <p><strong>Status:</strong> <span className={`badge ${team.status === 'approved' ? 'badge-success' : 'badge-warning'}`}>{team.status}</span></p>
            <p><strong>Members:</strong> {team.memberCount}</p>
            {team.club && (
              <p>
                <strong>Club:</strong>{' '}
                <Link 
                  to={`/clubs/${team.club.id}`} 
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                >
                  {team.club.name}
                </Link>
              </p>
            )}
            {team.league && (
              <p>
                <strong>League:</strong>{' '}
                <Link 
                  to={`/leagues?leagueId=${team.league.id}`} 
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#2E7D32', textDecoration: 'none', fontWeight: '500' }}
                >
                  {team.league.name}
                </Link>
              </p>
            )}
            {team.manager && (
              <p><strong>Manager:</strong> {team.manager.firstName} {team.manager.lastName}</p>
            )}
            {canPrintRoster(team) && (
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrintNextGameRoster(team.id);
                }}
                disabled={loadingRosters[team.id] || !nextMatches[team.id]}
                style={{ marginTop: '10px', width: '100%' }}
                title={!nextMatches[team.id] ? 'No upcoming match found' : ''}
              >
                {loadingRosters[team.id] ? 'Generating...' : nextMatches[team.id] ? '📄 Print Next Game Roster' : '📄 Print Next Game Roster (No Match)'}
              </button>
            )}
          </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Teams;

